import lodash from "lodash"
import dayjs from "dayjs"
import "dayjs/locale/th"
import buddhistEra from "dayjs/plugin/buddhistEra.js"
import isBetween from "dayjs/plugin/isBetween.js"
import { permissions } from "~/assets/fixedVariables"
import * as XLSX from "xlsx"
import { ProjectEventBudgetType } from "@/enums/event-budget-type.enum"

dayjs.locale("th")
dayjs.extend(buddhistEra)
dayjs.extend(isBetween)

export const { format: formatNumber } = Intl.NumberFormat("en-US", {
	minimumFractionDigits: 0,
	maximumFractionDigits: 0,
})
export const { format: formatMoney } = Intl.NumberFormat("en-US", {
	minimumFractionDigits: 2,
	maximumFractionDigits: 2,
})

const userStatuses: Array<{ value: string; title: string; color: string }> = [
	{ value: "activated", title: "ปกติ", color: "success" },
	{ value: "deactivated", title: "ถูกระงับ", color: "error" },
]
const userPermissions: Array<{ value: string; title: string; header: string }> = permissions

const userLoginMethods: Array<{ value: string; title: string }> = [
	{ value: "local", title: "Local" },
	{ value: "adservice", title: "AD service" },
]
const userRoles: Array<{ value: string; title: string }> = [
	{ value: "user", title: "ผู้ใช้งานทั่วไป" },
	{ value: "admin", title: "ผู้ดูแลระบบ" },
]
const projectStatuses: Array<{ value: string | null; title: string; color: string }> = [
	{ value: null, title: "ทั้งหมด", color: "gray" },
	{ value: "draft", title: "ฉบับร่าง", color: "gray" },
	{ value: "published", title: "เผยแพร่แล้ว", color: "success" },
	{ value: "closed", title: "ปิดโครงการ", color: "error" },
]
const budgetStatuses: Array<{ value: string | null; title: string; color: string }> = [
	{ value: null, title: "ทั้งหมด", color: "gray" },
	{ value: "normal", title: "ปกติ", color: "success" },
	{ value: "over", title: "เกินวงเงิน", color: "error" },
]
const strategicTypes: Array<{ value: string; title: string }> = [
	{ value: "strategic", title: "ยุทธศาสตร์" },
	{ value: "strategy", title: "กลยุทธ์" },
]
const sectionTypes: Array<{ value: string; title: string }> = [
	{ value: "section", title: "ฝ่าย/แผนก" },
	{ value: "agency", title: "ส่วนงาน" },
]
const showDeletedData: Array<{ value: string; title: string }> = [
	{ value: "", title: "ไม่แสดง" },
	{ value: "show", title: "แสดง" },
]
const masterDataStatus: Array<{ value: string | null; title: string; color?: string }> = [
	{ value: null, title: "ทั้งหมด" },
	{ value: "true", title: "เปิดใช้งาน", color: "success" },
	{ value: "false", title: "ปิดใช้งาน", color: "error" },
]
const eventBudgetTypes: Array<{ value: string | null; title: string }> = [
	{ value: ProjectEventBudgetType.BUDGET, title: "เงินงบประมาณปกติ" },
	{ value: ProjectEventBudgetType.NON_BUDGET, title: "เงินนอกงบประมาณ" },
	{ value: ProjectEventBudgetType.SAVING, title: "เงินสะสม" },
]
const fiscalYearCurrentAndEarlyYearOptions = new Array(30)
  .fill(0)
  .map((_, idx) => String(Number(new Date().getFullYear()) - idx + 543));

const fiscalYearNextYearOptions = new Array(1)
  .fill(0)
  .map((_, idx) => String(Number(new Date().getFullYear()) + idx + 1 + 543));

const fiscalYearOptions = [...fiscalYearNextYearOptions, ...fiscalYearCurrentAndEarlyYearOptions];

// remove ALL option to prevent overload server request
const tableItemsPerPageLimit = [
	{value: 10, title: '10'},
	{value: 25, title: '25'},
	{value: 50, title: '50'},
	{value: 100, title: '100'},
]

export const _ = lodash
export const useDayJS = dayjs
export const VFormatDate = (date: Date, format: string = "D MMM BBBB HH:mm") => useDayJS(date).format(format)
export const VFormatDateOnly = (date: Date, format: string = "D MMM BBBB") => useDayJS(date).format(format)
export const $assets = {
	userStatuses,
	userPermissions,
	userLoginMethods,
	userRoles,
	projectStatuses,
	budgetStatuses,
	strategicTypes,
	sectionTypes,
	showDeletedData,
	masterDataStatus,
	fiscalYearOptions,
	eventBudgetTypes,
	tableItemsPerPageLimit,
	getUserStatus: (value: any) => userStatuses.find((item) => item.value === value),
	getUserRole: (value: any) => userRoles.find((item) => item.value === value),
	getUserLoginMethod: (value: any) => userLoginMethods.find((item) => item.value === value),
	getProjectStatus: (value: any) => projectStatuses.find((item) => item.value === value),
	getStrategicType: (value: any) => strategicTypes.find((item) => item.value === value),
	getSectionType: (value: any) => sectionTypes.find((item) => item.value === value),
	getShowDeletedData: (value: any) => sectionTypes.find((item) => item.value === value),
}

export const exportAsExcel = (data: any[] = [], sheetName: string = "Sheet 1", fileName: string = "default") =>
	new Promise(async (resolve, reject) => {
		try {
			const worksheet = XLSX.utils.json_to_sheet(data)
			const workbook = XLSX.utils.book_new()
			XLSX.utils.book_append_sheet(workbook, worksheet, sheetName)

			const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "binary" })

			function s2ab(s: any) {
				const buf = new ArrayBuffer(s.length)
				const view = new Uint8Array(buf)
				for (let i = 0; i < s.length; i++) view[i] = s.charCodeAt(i) & 0xff
				return buf
			}

			const blob = new Blob([s2ab(wbout)], { type: "application/octet-stream" })
			const link = document.createElement("a")
			link.href = URL.createObjectURL(blob)
			link.download = `${fileName}.xlsx`
			link.click()
			return resolve(true)
		} catch (error: any) {
			return reject(error)
		}
	})

export const importExcelAsJSON = (event: any, header: string[]): Promise<{
	originalHeader: string[],
	data: Record<string, string>[]
}> =>
	new Promise(async (resolve, reject) => {
		try {
			const file = event.target.files ? event.target.files[0] : null;

			if (file) {
				const reader = new FileReader();

				reader.onload = (e) => {
					/* Parse data */
					if (!e.target) throw new Error('[import excel] event not found');

					const bstr = e.target.result;
					const workbook = XLSX.read(bstr, { type: "binary" });

					/* Get first worksheet */
					const worksheetName = workbook.SheetNames[0];
					const worksheet = workbook.Sheets[worksheetName];

					/* Convert array of arrays */
					const data = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

					const excelResult = convertExcelArrayToObjects(data as string[][], header);

					// resolve(excelResult);

					const result = {
						originalHeader: data[0] as string[],
						data: excelResult
					}

					resolve(result);
				};

				reader.readAsBinaryString(file);
			} else {
				throw new Error("กรุณาเลือกไฟล์");
			}
		} catch (error) {
			return reject(error);
		}
	});

export function convertExcelArrayToObjects(data: string[][], header: string[]) {
	if (!Array.isArray(data) || data.length < 2) {
		throw new Error("[excel convert] Invalid data format");
	}

	const records = data.slice(1); // remove header row

	return records.map((record) => {
		const obj: Record<string, string> = {};
		header.forEach((header, index) => {
			if (typeof record[index] === 'string') {
				obj[header] = record[index].trim();
			} else {
				obj[header] = record[index];
			}
		});
		return obj;
	});
}
	
export function isNotEmptyExcelCell(value: any) {
  if (value === null || value === undefined || value === "") {
    return false;
  }

  if (typeof value === "string" && value.trim() === "") {
    return false;
  }

  return true; // Only check for non-empty cell. invalid format is still allowed
}
